import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type { Grouping } from '../models/grouping.types'
import type { ConditionalMetric, CustomMetric } from '../models/metric.types'
import { definedMetricFields } from '../service/definedMetricFields'

export const useDefinitionsStore = defineStore('workspace-definitions', () => {
  const customMetrics = ref<CustomMetric[]>([])
  const conditionalMetrics = ref<ConditionalMetric[]>([])
  const groupings = ref<Grouping[]>([])
  const loadedWorkspaceId = ref<number | null>(null)

  const allDefinedMetricFields = computed(() =>
    definedMetricFields(customMetrics.value, conditionalMetrics.value),
  )

  function metricsByLevel(level: string): Array<CustomMetric | ConditionalMetric> {
    return [
      ...customMetrics.value.filter((metric) => metric.level === level),
      ...conditionalMetrics.value.filter((metric) => metric.level === level),
    ]
  }

  function groupingsByEntity(entity: string): Grouping[] {
    return groupings.value.filter((grouping) => grouping.entity_type === entity)
  }

  function setCustomMetrics(metrics: readonly CustomMetric[]): void {
    customMetrics.value = [...metrics]
  }

  function setConditionalMetrics(metrics: readonly ConditionalMetric[]): void {
    conditionalMetrics.value = [...metrics]
  }

  function setGroupings(nextGroupings: readonly Grouping[]): void {
    groupings.value = [...nextGroupings]
  }

  function markLoaded(workspaceId: number | string): void {
    const numericId = Number(workspaceId)
    loadedWorkspaceId.value = Number.isFinite(numericId) ? numericId : null
  }

  function resetState(): void {
    customMetrics.value = []
    conditionalMetrics.value = []
    groupings.value = []
    loadedWorkspaceId.value = null
  }

  /** Drop vocabulary when the selected workspace no longer matches the hydrated owner. */
  function invalidate(workspaceId?: number | string): void {
    if (workspaceId === undefined || loadedWorkspaceId.value !== Number(workspaceId)) resetState()
  }

  return {
    customMetrics,
    conditionalMetrics,
    groupings,
    loadedWorkspaceId,
    allDefinedMetricFields,
    metricsByLevel,
    groupingsByEntity,
    setCustomMetrics,
    setConditionalMetrics,
    setGroupings,
    markLoaded,
    invalidate,
    resetState,
  }
})

export type DefinitionsStore = ReturnType<typeof useDefinitionsStore>
